/**
 * PhotoUpload — circular avatar dropzone with preview + Storage upload.
 */

import { useEffect, useRef, useState } from "react";
import { User as UserIcon, CircleNotch as Loader2 } from "@phosphor-icons/react";
import { useSupabaseClient } from "@/lib/supabase";
import { useProfileId } from "../../hooks/useProfileId";
import { uploadAvatar } from "../../api/businessContext";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

export interface PhotoUploadProps {
  value?: string | null;
  onChange: (publicUrl: string) => void;
  disabled?: boolean;
}

export function PhotoUpload({ value, onChange, disabled }: PhotoUploadProps) {
  const { client } = useSupabaseClient();
  const { profileId, isReady } = useProfileId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(value ?? null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setPreview(value ?? null);
  }, [value]);

  const handleFile = async (file: File) => {
    if (!isReady || !profileId) {
      toast.error("Aguardando autenticação…");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setIsUploading(true);
    try {
      const { publicUrl } = await uploadAvatar({ client, file, userId: profileId });
      onChange(publicUrl);
      toast.success("Foto atualizada");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Falha no upload";
      toast.error(msg);
      setPreview(value ?? null);
    } finally {
      setIsUploading(false);
      URL.revokeObjectURL(objectUrl);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isUploading}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        }}
        className={cn(
          "relative flex size-24 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border/80 bg-muted/30 transition hover:border-primary/60",
          disabled && "cursor-not-allowed opacity-50",
        )}
        data-testid="photo-upload"
        aria-label="Enviar foto"
      >
        {preview ? (
          <img src={preview} alt="" className="size-full object-cover" />
        ) : (
          <UserIcon className="size-8 text-muted-foreground" strokeWidth={1.5} />
        )}
        {isUploading ? (
          <span className="absolute inset-0 flex items-center justify-center bg-background/70">
            <Loader2 className="size-5 animate-spin text-primary" />
          </span>
        ) : null}
      </button>
      <div className="text-xs text-muted-foreground">
        <p>JPG, PNG ou WebP</p>
        <p>até 2MB</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = "";
        }}
        data-testid="photo-upload-input"
      />
    </div>
  );
}
